import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RippLayout } from "./ui";
import { nftFetch } from "./wallet";
import { Countdown } from "./token";

const TAG = { fontFamily: "'Permanent Marker', cursive" };

const CHAPTERS = [
  ["01", "The wall", "Every city has one. A wall by the train where every crew puts up their name. Names get painted over every week. Only one mark never got buffed: a three-point crown, sprayed gold, dripping at the base. Nobody knows who painted it first. The rule was always the same — you don't put the crown up, you earn it."],
  ["02", "The tape", "The market is that wall. Thousands of tokens get tagged on it every day and most are painted over by morning — rugs, bundles, scanner bots shouting at each other. A few runners stay up. The whole game is seeing which ones before everybody else does."],
  ["03", "The machine that never sleeps", "RIPP was built to read that wall at 3 a.m. — and every other wall at once. It is not one bot. It is the most advanced trading machine out there, attacking the market from every angle at the same time: memes, Bitcoin, the stock market, the wallets that move them and the launches before anyone sees them. It never gets tired, never gets greedy, never falls for a scanner bot's hype. Every entry and every exit is logged where anyone can check."],
  ["04", "The crown comes back", "RIPP doesn't flex wins it can't show. It sees the runners, gets in, gets paid and gets out before the lights go off. That's what the crown always meant on that wall. Three points, three rules. Now the crown is on the token — $RIPP — and it goes to the people who ride with us."],
];

const POWERS = [
  ["THE MEME HUNTER", "Its own superpower. Scans every new Solana token, rates the dev, the holders, the liquidity and the crowd, then enters and exits on its own — in before the run, out before the rug."],
  ["THE MARKET TRADER", "A separate superpower for Bitcoin and the stock market. Tracks the market around the clock for the optimal position, sizes it, enters and exits with discipline — no emotion, no hero bags."],
  ["THE WALLET TRACKER", "Follows the wallets that move markets. When they buy, RIPP knows in seconds. When they sell, RIPP is already out."],
  ["THE LAUNCH SNIPER", "Hunts brand-new launches from proven creators and fast movers, validates them, and enters early — before the timeline catches on."],
  ["THE HIVE", "Never sleeps. Reads X, Telegram and the chain every minute and feeds a constant stream of data to the Core."],
  ["THE CORE", "Where every signal lands and every decision is made. One brain, every angle, one goal: results."],
];

// Cumulative: every tier keeps everything below it. live = available to holders today; otherwise "rolling out".
const ACCESS = [
  { tier: "COMMON", lvl: "CORE", color: "#94a3b8", perks: [["Hive calls", "Live meme calls straight from the hive, in the holders' feed and group chat", true], ["Holder badge + community", "Your verified RIPP badge and the RIPP Labs crew", true]] },
  { tier: "UNCOMMON", lvl: "CORE+", color: "#34d399", perks: [["Copy trading", "Mirror all the top influencer wallets the hive follows, within seconds", true], ["Voice alerts", "Spoken buy, sell and take-profit calls the moment they happen", true]] },
  { tier: "RARE", lvl: "HIVE", color: "#38bdf8", perks: [["Full meme trading automation", "The Solana meme engine on your own wallet: rug filter, sizing, 2x take-profit and moonbag exits", true], ["Runner Radar", "See the runners forming before the timeline does", true]] },
  { tier: "EPIC", lvl: "HIVE PRO", color: "#c084fc", perks: [["Bitcoin, Nasdaq + all futures engines", "Daily-trend gating, Mag 7 bias, liquidity-sweep entries and winner stacking", true]] },
  { tier: "LEGENDARY", lvl: "ELITE · EVERYTHING", color: "#F5C451", perks: [["Options + stocks engine", "Strike, expiry and Greeks picked by the hive; stock trading included", true], ["Hive growth automation for your socials", "Your own X posts, engine clips, outreach and Telegram group bot running 24/7", false], ["Everything RIPP does", "Every engine, every feed, every new power the day it ships", true]] },
];

const RULES = [["GET IN", "Catch the runner early — the Hive, GrokBot and the wallets we follow see it first."], ["GET PAID", "Size it right, take the move, no hero bags."], ["GET OUT", "Exit before the dev, the bundle or the bots do. Receipts over vibes."]];

const CREW = [
  ["oracle", "The Oracle", "Old as the block. Sees the narrative before the timeline does."],
  ["hunter", "The Hunter", "Hood up, binoculars out. First on every new launch."],
  ["frog", "The Frog", "Pure degen swagger. Lives on the trenches feed."],
  ["bear", "The Bear", "Calm, cold, calculated. Guards the risk and the crystal."],
  ["bull", "The Bull", "The muscle of the crew. Charges when the trend is real."],
];

function Hero({ t }) {
  return (
    <section className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center" data-testid="lore-hero">
      <div>
        <div className="text-[11px] tracking-[0.4em] text-[#F5C451]/80 mb-6">$RIPP · THE LORE</div>
        <h1 className="text-5xl lg:text-7xl font-black leading-[0.95] tracking-tight">Street built.<br /><span className="text-[#F5C451]" style={TAG}>Crown earned.</span></h1>
        <p className="mt-8 text-white/60 max-w-xl text-sm lg:text-base leading-relaxed" data-testid="lore-intro">
          Before the token, before the bot, there was a gold crown on a wall nobody could buff. This is where $RIPP comes from.
        </p>
        <LaunchClock id="hero" t={t} />
      </div>
      <img src="/lore/crown_graffiti.jpg" alt="The RIPP graffiti crown" className="w-full max-w-md mx-auto rounded-3xl border border-[#F5C451]/20 shadow-[0_0_80px_rgba(245,196,81,0.15)] lore-float" data-testid="lore-crown-img" />
    </section>
  );
}

function Chapters() {
  return (
    <section className="mt-28 space-y-16" data-testid="lore-chapters">
      {CHAPTERS.map(([n, title, body], i) => (
        <div key={n} className={`grid md:grid-cols-[120px_1fr] gap-6 max-w-3xl ${i % 2 ? "md:ml-auto" : ""} lore-rise`} style={{ animationDelay: `${i * 120}ms` }} data-testid={`lore-chapter-${n}`}>
          <div className="text-6xl font-black text-[#F5C451]/25" style={TAG}>{n}</div>
          <div>
            <h2 className="text-base md:text-lg font-bold tracking-[0.2em] text-[#F5C451] uppercase">{title}</h2>
            <p className="mt-3 text-white/65 leading-relaxed text-sm lg:text-base">{body}</p>
          </div>
        </div>
      ))}
    </section>
  );
}

function Mascot() {
  return (
    <section className="mt-32 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center" data-testid="lore-mascot">
      <video src="/lore/mav_turntable.mp4" poster="/lore/bull_mascot.jpg" autoPlay loop muted playsInline className="w-full max-w-md rounded-3xl border border-[#F5C451]/25 bg-black" data-testid="lore-mascot-img" />
      <div>
        <div className="text-[11px] tracking-[0.4em] text-[#F5C451]/80">THE MASCOT · LEGENDARY</div>
        <h2 className="mt-4 text-4xl lg:text-5xl font-black" style={TAG} data-testid="lore-mascot-name">Mav</h2>
        <p className="mt-6 text-white/65 leading-relaxed text-sm lg:text-base max-w-xl">
          Short for Maverick — the one who never runs with the herd. Mav is the Legendary Bull of the RIPP Genesis collection — gold horns, gold-trimmed jacket, the RIPP chain he never takes off. When the market charges, Mav charges first. When it turns, he's already out.
        </p>
      </div>
    </section>
  );
}

function Powers() {
  return (
    <section className="mt-32" data-testid="lore-powers">
      <div className="text-[11px] tracking-[0.4em] text-[#F5C451]/80">THE POWER OF RIPP</div>
      <h2 className="mt-4 text-3xl lg:text-5xl font-black max-w-3xl" style={TAG}>An earning machine. The only one of its kind.</h2>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {POWERS.map(([t, d], i) => (
          <div key={t} className={`rounded-2xl border p-6 transition-colors duration-300 hover:border-[#F5C451]/60 ${t === "THE CORE" ? "border-[#F5C451]/50 bg-[#141005]" : "border-white/10 bg-[#0b0b0b]"}`} data-testid={`lore-power-${i}`}>
            <div className="text-xs font-bold tracking-[0.3em] text-[#F5C451]">{t}</div>
            <p className="mt-3 text-white/60 text-sm leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Perk({ name, d, live }) {
  return (
    <li className="flex items-start gap-3">
      <span className={`mt-0.5 w-[96px] shrink-0 whitespace-nowrap text-center rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.18em] ${live ? "bg-emerald-400/15 text-emerald-300" : "bg-white/5 text-white/45"}`}>{live ? "LIVE" : "ROLLING OUT"}</span>
      <span><span className="text-sm font-semibold text-white/90">{name}</span><span className="block text-xs text-white/50">{d}</span></span>
    </li>
  );
}

function Access() {
  return (
    <section className="mt-32" data-testid="lore-access">
      <div className="text-[11px] tracking-[0.4em] text-[#F5C451]/80">HOLD A RIPP · UNLOCK THE MACHINE</div>
      <h2 className="mt-4 text-3xl lg:text-4xl font-black" style={TAG}>Your NFT is your key.</h2>
      <p className="mt-4 text-white/55 text-sm lg:text-base max-w-2xl">The rarer the RIPP, the deeper you go into the machine. Every tier keeps everything below it, and Legendary unlocks everything.</p>
      <div className="mt-8 divide-y divide-white/10 rounded-2xl border border-white/10 bg-[#0b0b0b] overflow-hidden">
        {ACCESS.map(({ tier, lvl, color, perks }, i) => (
          <div key={tier} className={`grid md:grid-cols-[170px_1fr] gap-4 px-6 py-5 ${tier === "LEGENDARY" ? "bg-[#141005]" : ""}`} data-testid={`lore-access-${tier.toLowerCase()}`}>
            <div>
              <div className="text-sm font-black tracking-[0.2em]" style={{ color }}>{tier}</div>
              <div className="mt-1 text-[10px] tracking-[0.25em] text-white/40">{lvl}</div>
              {i > 0 && <div className="mt-2 text-[10px] text-white/35">+ everything in {ACCESS[i - 1].tier.toLowerCase()}</div>}
            </div>
            <ul className="space-y-3">{perks.map(([n, d, live]) => <Perk key={n} name={n} d={d} live={live} />)}</ul>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-white/40 max-w-2xl" data-testid="lore-access-note">Genesis founder pieces add the RIPP founder badge and their Genesis serial on top of their tier. Hive growth automation for your own socials is rolling out and switches on for Legendary holders as it ships. Holding a RIPP is not financial advice or a promise of returns.</p>
    </section>
  );
}

function Crown() {
  return (
    <section className="mt-32" data-testid="lore-rules">
      <h2 className="text-base md:text-lg font-bold tracking-[0.3em] text-white/50">THREE POINTS · THREE RULES</h2>
      <div className="mt-8 grid md:grid-cols-3 gap-5">
        {RULES.map(([r, d], i) => (
          <div key={r} className="rounded-2xl border border-white/10 bg-[#0b0b0b] p-6 transition-colors duration-300 hover:border-[#F5C451]/50" data-testid={`lore-rule-${i}`}>
            <div className="text-2xl text-[#F5C451]" style={TAG}>{r}</div>
            <p className="mt-3 text-white/55 text-sm leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Crew() {
  return (
    <section className="mt-32" data-testid="lore-crew">
      <h2 className="text-base md:text-lg font-bold tracking-[0.3em] text-white/50">THE CREW · RIPP GENESIS</h2>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
        {CREW.map(([k, n, d]) => (
          <div key={k} className="group rounded-2xl border border-white/10 bg-[#0b0b0b] overflow-hidden" data-testid={`lore-crew-${k}`}>
            <img src={`/lore/${k}.jpg`} alt={n} className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="p-4"><div className="text-sm font-bold text-[#F5C451]">{n}</div><p className="mt-1 text-[12px] text-white/50 leading-snug">{d}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function LaunchClock({ id = "token", t }) {
  if (!t || t.tba || !t.token_launch_ts) return null;
  if (t.contract_address) return <div className="mt-6 text-sm font-bold text-[#F5C451]" data-testid={`lore-${id}-live`}>$RIPP is live. <Link to="/ripp/token" className="underline">Get the official CA →</Link></div>;
  return (
    <div className="mt-8 flex flex-wrap items-end gap-10 rounded-2xl border border-[#F5C451]/20 bg-black/30 p-6" data-testid={`lore-${id}-countdown`}>
      <Countdown ts={t.token_launch_ts} label="$RIPP TOKEN LAUNCH" />
      <div className="text-xs text-white/50 pb-1">{t.token_launch_et} · Pump.fun</div>
    </div>
  );
}

function Token({ t }) {
  return (
    <section className="mt-32 mb-10 rounded-3xl border border-[#F5C451]/25 bg-[#0d0a04] p-8 lg:p-12" data-testid="lore-token">
      <h2 className="text-3xl lg:text-4xl font-black" style={TAG}>The crown is the token.</h2>
      <LaunchClock t={t} />
      <ul className="mt-6 space-y-2 text-white/65 text-sm lg:text-base">
        <li>· $RIPP fair-launches on Pump.fun — no presale, no team allocation.</li>
        <li>· It launches Friday, November 6 at 12:00 PM ET. Full details on the <Link to="/ripp/token" className="text-[#F5C451] underline" data-testid="lore-token-countdown-link">token page</Link>.</li>
        <li>· The Genesis mint opens 72 hours after the token. Genesis holders get first access to every future drop.</li>
        <li>· The official contract address is posted only in t.me/Ripplabs and on x.com/RippLabs_.</li>
      </ul>
      <div className="mt-8 flex flex-wrap gap-4">
        <a href="https://t.me/+FZyNGifYYd0zNWUx" target="_blank" rel="noreferrer" className="px-6 py-3 rounded-full bg-[#F5C451] text-black text-xs font-bold tracking-widest transition-transform duration-200 hover:-translate-y-0.5" data-testid="lore-telegram-btn">JOIN THE CHANNEL</a>
        <Link to="/ripp/token" className="px-6 py-3 rounded-full border border-[#F5C451]/60 text-[#F5C451] text-xs font-bold tracking-widest hover:bg-[#F5C451]/10" data-testid="lore-token-btn">$RIPP TOKEN PAGE</Link>
        <Link to="/ripp/roadmap" className="px-6 py-3 rounded-full border border-white/15 text-xs font-bold tracking-widest hover:border-[#F5C451]/50" data-testid="lore-roadmap-btn">THE ROADMAP →</Link>
      </div>
    </section>
  );
}

export function RippLore() {
  const [t, setT] = useState(null);
  useEffect(() => { nftFetch("/api/nft/token").then(setT).catch(() => {}); }, []);
  return (
    <RippLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Permanent+Marker&display=swap');
        @keyframes loreRise{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}} .lore-rise{animation:loreRise .7s ease-out both}
        @keyframes loreFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}} .lore-float{animation:loreFloat 6s ease-in-out infinite}`}</style>
      <Hero t={t} /><Chapters /><Powers /><Mascot /><Access /><Crown /><Crew /><Token t={t} />
    </RippLayout>
  );
}
